import { h as __h } from "preact";
import { Component, h } from "preact";
import { relayResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, response } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import adminStyle from "~/admin/lib/style.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./editRelay.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./editRelay.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @typedef {(json: {
 * 	name: string;
 * 	description: string;
 * 	private_address: string;
 * 	private_port: number;
 * 	public_address: string;
 * 	public_port: number;
 * }) => Promise<relayResponse>} SubmitCallback
 */

/**
 * @param {{ orgId: string }} props
 * @param {relayResponse} relay
 */
export const editRelay = async ({
  orgId
}, relay) => {
  /** @type {SubmitCallback} */
  const onSubmit = async json => {
    const {
      data
    } = await authedApiRequest("PATCH", `/v2/orgs/${orgId}/relays/${relay.id}`, {
      json,
      schema: response(relayResponse)
    });
    Query.invalidate([`/v2/orgs/${orgId}/relays`], [`/v2/orgs/${orgId}/relays/${relay.id}`]);
    alert(`Updated relay '${data.name}'`, {
      type: "success"
    });
    return data;
  };
  return await modal({
    title: "Edit relay",
    content: (/** @type {(result?: relayResponse) => void} */resolve) => h(ModalChildren, {
      resolve,
      relay,
      onSubmit
    })
  });
};

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {relayResponse} relay
 * @property {(result?: relayResponse) => void} resolve
 * @property {SubmitCallback} onSubmit
 */
class ModalChildren extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const raw = Object.fromEntries(form.entries());
    const data = {
      name: (/** @type {string} */raw.name),
      description: (/** @type {string} */raw.description),
      private_address: (/** @type {string} */raw.private_address),
      private_port: Number(raw.private_port),
      public_address: (/** @type {string} */raw.public_address),
      public_port: Number(raw.public_port)
    };
    this.props.resolve(await this.props.onSubmit(data));
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);
    const relay = this.props.relay;
    return __h("form", {
      "class": adminStyle.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h(Field, {
      "for": "name",
      label: "Name",
      error: error?.fields.name?.message
    }, __h("input", {
      "class": input.input,
      id: "name",
      name: "name",
      required: true,
      defaultValue: relay.name,
      autofocus: true
    })), __h(Field, {
      "for": "description",
      label: "Description",
      error: error?.fields.description?.message
    }, __h("input", {
      "class": input.input,
      id: "description",
      name: "description",
      defaultValue: relay.description
    })), __h("section", {
      "class": css.addressSection
    }, __h("header", {
      "class": css.sectionHeader
    }, __h("h3", {
      "class": css.sectionTitle
    }, "Private address"), __h("p", {
      "class": css.sectionDesc
    }, "The IP address of the Linux machine running the relay. Used by devices inside your\n\t\t\t\t\t\t\tnetwork to connect. By default, the Phaze Relay application listens on port 41000.")), __h("div", {
      "class": css.ipFields
    }, __h(Field, {
      "for": "private_address",
      label: "Address",
      error: error?.fields.private_address?.message
    }, __h("input", {
      "class": input.input,
      id: "private_address",
      name: "private_address",
      required: true,
      defaultValue: relay.private_address
    })), __h(Field, {
      "for": "private_port",
      label: "Port",
      error: error?.fields.private_port?.message
    }, __h("input", {
      "class": input.input,
      id: "private_port",
      name: "private_port",
      type: "number",
      min: "1",
      max: "65535",
      required: true,
      defaultValue: relay.private_port
    })))), __h("section", {
      "class": css.addressSection
    }, __h("header", {
      "class": css.sectionHeader
    }, __h("h3", {
      "class": css.sectionTitle
    }, "Public address"), __h("p", {
      "class": css.sectionDesc
    }, "The public IP and port used by devices outside your network. Usually your router or\n\t\t\t\t\t\t\tfirewall's public IP with a port forward pointing to the relay machine.")), __h("div", {
      "class": css.ipFields
    }, __h(Field, {
      "for": "public_address",
      label: "Address",
      error: error?.fields.public_address?.message
    }, __h("input", {
      "class": input.input,
      id: "public_address",
      name: "public_address",
      required: true,
      defaultValue: relay.public_address
    })), __h(Field, {
      "for": "public_port",
      label: "Port",
      error: error?.fields.public_port?.message
    }, __h("input", {
      "class": input.input,
      id: "public_port",
      name: "public_port",
      type: "number",
      min: "1",
      max: "65535",
      required: true,
      defaultValue: relay.public_port
    })))), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Update relay"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}